import { useEffect, useId, useState } from "react";
import { Button, EmptyState, Pagination } from "../../../ui";
import { ChevronDown } from "../../../ui/icons";
import RequestRow from "./RequestRow";
import { MAX_WINDOW, PAGE_SIZE, useRequestSection } from "./useUnifiedRequests";

// One expandable section ("In Progress" / "Completed"): its own server-side
// page (20 per page), its own scroll area, its own loading and error state.
export default function RequestSection({ group, title, storeId, reloadToken, t, lang, branches, role, highlightIds = [], onChanged, onOpenFile, onReport, defaultOpen = true }) {
  const id = useId();
  const [open, setOpen] = useState(defaultOpen);
  const { phase, data, error, page, setPage, reload } = useRequestSection({ group, storeId, reloadToken });
  const items = data?.items || [];
  const total = data?.total;
  const done = group === "completed";
  const count = total ?? items.length;
  useEffect(() => {
    onReport?.(group, phase === "ready" || phase === "refreshing" ? "ready" : phase, count);
  }, [onReport, group, phase, count]);

  return (
    <section className={`lud-ur-section-block${open ? " is-open" : ""}`} aria-labelledby={`${id}-title`}>
      <h3 className="lud-ur-section-head">
        <button type="button" id={`${id}-title`} className="lud-ur-section-toggle" aria-expanded={open} aria-controls={`${id}-body`} onClick={() => setOpen((value) => !value)}>
          <ChevronDown size={18} aria-hidden="true" className="lud-ur-chevron" />
          <span>{title}</span>
          <span className="lud-ur-count" aria-label={t.countLabel(total ?? 0)}>{total ?? "…"}</span>
        </button>
      </h3>
      <div id={`${id}-body`} className="lud-ur-section-body" hidden={!open}>
        {phase === "loading" && !items.length && <EmptyState variant="loading" compact>{t.loading}</EmptyState>}
        {phase === "error" && !items.length && (
          <EmptyState variant="error" compact title={t.errorTitle} action={<Button size="sm" onClick={reload}>{t.retry}</Button>}>
            {error?.status === 403 ? t.forbidden : error?.code === "NETWORK" ? t.network : error?.message}
          </EmptyState>
        )}
        {phase === "ready" && !items.length && <p className="lud-ur-empty">{t.emptySection[group]}</p>}
        {items.length > 0 && (
          <div className={`lud-ur-scroll${phase === "refreshing" ? " is-busy" : ""}`} aria-busy={phase === "refreshing" || undefined} tabIndex={0} role="region" aria-label={title}>
            <ul className="lud-ur-rows">
              {items.map((item) => (
                <RequestRow key={item.key} item={item} t={t} lang={lang} branches={branches} done={done} role={role} highlighted={highlightIds.includes(item.identity.id)} onChanged={onChanged} onOpenFile={onOpenFile} />
              ))}
            </ul>
          </div>
        )}
        {phase === "error" && items.length > 0 && <p className="lud-ur-error" role="alert">{error?.message || t.errorTitle}</p>}
        {items.length > 0 && (total > PAGE_SIZE || page > 1) && (
          <Pagination
            className="lud-ur-pagination"
            page={page}
            pages={data?.page?.pages || null}
            total={total ?? null}
            hasMore={Boolean(data?.page?.hasMore)}
            maxPage={Math.floor(MAX_WINDOW / PAGE_SIZE)}
            disabled={phase === "refreshing"}
            onPage={setPage}
            labels={t.pagination}
          />
        )}
      </div>
    </section>
  );
}
